// External
import { useState, useEffect, useContext, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { MdArrowBack } from "react-icons/md";
import { FiCheck } from "react-icons/fi";
import { FaEdit } from "react-icons/fa";
import { AiOutlineClose } from "react-icons/ai";
// Internal
import { AuthContext } from "../../context/auth-context";
import { DataContext } from "../../context/data-context";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import LinkPair from "../UI/LinkPair";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import uploadFile from "../../util/upload-file";
import Switch from "../UI/Switch";
import { toTitleCase } from "../../util/toTitleCase";
import {
  activateVehicle,
  deactivateVehicle,
  getVehicleStats,
} from "../../api/fetch";
import { editPartnerVehicle } from "../../api/add";
import { format } from "date-fns";

export default function PartnerVehicle({ vehicle, setVehicle, partner }) {
  const { user } = useContext(AuthContext);
  const { fetchPartners } = useContext(DataContext);

  const navigate = useNavigate();

  // Initialize States
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState(false);
  const [edit, setEdit] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [item, setItem] = useState();
  const [financed, setFinanced] = useState(vehicle.financed);

  // ====================================== EDIT PARTNER VEHICLE
  const editHandler = async () => {
    try {
      setLoading(true);
      const updates = {
        financed: document.getElementById("Financed").value || false,
        license: document.getElementById("License").value || false,
        state: document.getElementById("State").value || false,
        amount: document.getElementById("Finance Amount").value || false,
        term: document.getElementById("Term (Months)").value || false,
        institution: document.getElementById("Financing Institution").value || false,
        monthly: document.getElementById("Monthly Payment").value || false,
        due: document.getElementById("Due Date").value || false,
      };

      // Get Files
      const files = {
        bos: document.getElementById("Bill of Sale").files[0],
        title: document.getElementById("Title").files[0],
        registeration: document.getElementById("Registeration").files[0],
        vir: document.getElementById("Inspection Report").files[0],
        insurance: document.getElementById("Insurance").files[0],
        poa: document.getElementById("Power of Attorney").files[0],
        loan: document.getElementById("Loan Document").files[0],
      };

      // Upload Files
      if (files.bos) {
        const path = `partners/${user.uid}/vehicles/${
          vehicle.vin
        }/bos.${files.bos.name.split(".").pop()}`;
        setItem("Bill of Sale");
        updates.bos = await uploadFile(path, files.bos, setUploading);
        setUploading(false);
      }
      if (files.title) {
        const path = `partners/${user.uid}/vehicles/${
          vehicle.vin
        }/title.${files.title.name.split(".").pop()}`;
        setItem("Title Documentation");
        updates.title = await uploadFile(path, files.title, setUploading);
        setUploading(false);
      }
      if (files.registeration) {
        const path = `partners/${user.uid}/vehicles/${
          vehicle.vin
        }/registeration.${files.registeration.name.split(".").pop()}`;
        setItem("Registeration");
        updates.registeration = await uploadFile(
          path,
          files.registeration,
          setUploading
        );
        setUploading(false);
      }
      if (files.vir) {
        const path = `partners/${user.uid}/vehicles/${
          vehicle.vin
        }/vir.${files.vir.name.split(".").pop()}`;
        setItem("Inspection Report");
        updates.vir = await uploadFile(path, files.vir, setUploading);
        setUploading(false);
      }
      if (files.insurance) {
        const path = `partners/${user.uid}/vehicles/${
          vehicle.vin
        }/insurance.${files.insurance.name.split(".").pop()}`;
        setItem("Insurance");
        updates.insurance = await uploadFile(
          path,
          files.insurance,
          setUploading
        );
        setUploading(false);
      }
      if (files.poa) {
        const path = `partners/${user.uid}/vehicles/${
          vehicle.vin
        }/poa.${files.poa.name.split(".").pop()}`;
        setItem("Power of Attorney");
        updates.poa = await uploadFile(path, files.poa, setUploading);
        setUploading(false);
      }
      if (files.loan) {
        const path = `partners/${user.uid}/vehicles/${
          vehicle.vin
        }/loan.${files.loan.name.split(".").pop()}`;
        setItem("Loan Document");
        updates.loan = await uploadFile(path, files.loan, setUploading);
        setUploading(false);
      }

      // Upload Changes
      const response = await editPartnerVehicle(user, {
        partnerId: partner.id,
        vin: vehicle.vin,
        items: updates,
      });
      fetchPartners(() => navigate(`/partners/info?id=${partner.id}`));
    } catch (err) {
      console.log(err);
      setLoading(false);
      setWarning(err.message);
    }
  };

  // ====================================== DELETE VEHICLE DOCUMENT
  const deleteHandler = async (key) => {
    try {
      setLoading(true);
      const response = await editPartnerVehicle(user, {
        partnerId: partner.id,
        vin: vehicle.vin,
        items: { [key]: "delete" },
      });
      fetchPartners(() => navigate(`/partners/info?id=${partner.id}`));
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      setWarning(err.message);
    }
  };

  // ====================================== ACTVIATE PARTNER VEHICLE
  const activateVehicleHandler = async () => {
    try {
      setLoading(true);
      const response = await activateVehicle(user, {
        vin: vehicle.vin,
        partnerId: partner.id,
      });
      fetchPartners(() => navigate(`/partners/info?id=${partner.id}`));
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      setWarning(err.message);
    }
  };

  // ====================================== DEACTVIATE PARTNER VEHICLE
  const deactivateVehicleHandler = async () => {
    try {
      setLoading(true);
      const response = await deactivateVehicle(user, {
        vin: vehicle.vin,
        partnerId: partner.id,
      });
      fetchPartners(() => navigate(`/partners/info?id=${partner.id}`));
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      setWarning(err.message);
    }
  };

  return (
    <div className="container">
      <Heading title="Partner Vehicle">
        {edit ? (
          <Fragment>
            <FiCheck
              onClick={editHandler}
              className="text-lg text-white cursor-pointer md:block hover:text-stone-400"
            />
            <AiOutlineClose
              onClick={() => setEdit(false)}
              className="text-lg text-white cursor-pointer hover:text-stone-400"
            />
          </Fragment>
        ) : (
          <Fragment>
            <MdArrowBack
              onClick={() => setVehicle()}
              className="text-lg text-white cursor-pointer hover:text-stone-400"
            />
            <FaEdit
              onClick={() => setEdit(true)}
              className="text-lg text-white cursor-pointer hover:text-stone-400"
            />
          </Fragment>
        )}
      </Heading>

      <div className="grid">
        {loading ? (
          <LoadingSpinnerWrapper />
        ) : (
          <Fragment>
            <div className="items-center info">
              <DetailPair
                label="ID"
                value={vehicle.id}
                link={vehicle.id ? `/vehicles/info?id=${vehicle.id}` : false}
              />
              <DetailPair
                label="Vehicle"
                value={`${vehicle.make} ${vehicle.model} ${vehicle.year}`}
                link={vehicle.id ? `/vehicles/info?id=${vehicle.id}` : false}
              />
              <DetailPair label="VIN" value={vehicle.vin} />
              <DetailPair label="Year" value={vehicle.year} />
              <DetailPair label="License" value={vehicle.license} edit={edit} />
              <DetailPair label="State" value={vehicle.state} edit={edit} />
              <DetailPair label="Status" value={toTitleCase(vehicle.status)} />
              <LinkPair label="Bill of Sale" link={vehicle.bos} edit={edit} />
              <LinkPair label="Title" link={vehicle.title} edit={edit} />
              <LinkPair
                label="Registeration"
                link={vehicle.registeration}
                edit={edit}
              />
              <LinkPair
                label="Inspection Report"
                link={vehicle.vir}
                edit={edit}
              />
              <LinkPair
                label="Insurance"
                link={vehicle.insurance}
                edit={edit}
              />
              <LinkPair
                label="Power of Attorney"
                link={vehicle.poa}
                edit={edit}
              />
              <LinkPair
                label="Vehicle Agreement"
                link={vehicle.agreement}
                edit={edit}
              />

              <div className="flex items-center space-x-4">
                <Switch
                  label="Financed"
                  onSwitch={setFinanced}
                  value={financed}
                  edit={edit}
                />
              </div>

              {financed && (
                <Fragment>
                  <DetailPair
                    label="Financing Institution"
                    value={vehicle.institution}
                    edit={edit}
                  />
                  <DetailPair
                    label="Finance Amount"
                    value={vehicle.amount}
                    edit={edit}
                  />
                  <DetailPair
                    label="Term (Months)"
                    value={vehicle.term}
                    edit={edit}
                  />
                  <DetailPair
                    label="Monthly Payment"
                    value={vehicle.monthly}
                    edit={edit}
                  />
                  <DetailPair
                    label="Due Date"
                    value={`${
                      vehicle.due
                        ? format(new Date(vehicle.due), "YYYY-mm-dd")
                        : "-"
                    }`}
                    edit={edit}
                    type={"date"}
                  />
                  <LinkPair
                    label="Loan Document"
                    link={vehicle.loan}
                    edit={edit}
                  />
                </Fragment>
              )}
            </div>
            {!edit && (
              <div className="mt-12 info">
                {vehicle.poa && (
                  <button
                    onClick={() => deleteHandler("poa")}
                    className="btn-secondary"
                  >
                    Reassign POA
                  </button>
                )}
                {vehicle.agreement && (
                  <button
                    onClick={() => deleteHandler("agreement")}
                    className="btn-secondary"
                  >
                    Reassign Agreement
                  </button>
                )}
                {vehicle.status !== "active" ? (
                  <button
                    onClick={activateVehicleHandler}
                    className="btn-primary"
                  >
                    Activate Vehicle
                  </button>
                ) : (
                  <button
                    onClick={deactivateVehicleHandler}
                    className="btn-warning"
                  >
                    Deactivate Vehicle
                  </button>
                )}
                {warning && <p className="text-red-600">{warning}</p>}
              </div>
            )}
          </Fragment>
        )}
        {uploading && (
          <p className="text-stone-400">
            Uploading {item}: {uploading}%
          </p>
        )}
      </div>
    </div>
  );

  // aba3041c-affa-4432-b0c4-df96a5fa9274&state=eyJpbnN0YW5jZUlkIjoiMTcwODQxODYxODY3Njk0MTA0ODcwIn0%3D
}
